import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { diffLines } from "diff";
import { Edit3 } from "lucide-react";
import { useMemo } from "react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { ExpandableMessageCard, FileName, SimpleMessageCard } from "./shared";
import { getProjectVersionIdFromResult } from "./shared/get-project-version-id";
import { useFileLinkProps } from "./useFileLinkProps";

interface StringReplaceProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
  messageId: string;
}

export function StringReplace({ part, messageId }: StringReplaceProps) {
  const { old_str, new_str } = part.args ?? {};
  const path = part.args?.path as string;

  const oldStr = typeof old_str === "string" ? old_str : "";
  const newStr = typeof new_str === "string" ? new_str : "";

  const projectId = useActiveProjectId();
  const snapshotId = getProjectVersionIdFromResult(part);
  const linkProps = useFileLinkProps(
    projectId,
    messageId,
    snapshotId,
    path || "",
  );

  const hasContent = !!oldStr || !!newStr;
  const fileLink = (
    <FileName
      filename={path}
      linkProps={linkProps}
      variant={hasContent ? "outline" : "default"}
    />
  );

  const title =
    part.status === "complete" || part.status === "error"
      ? "Edited"
      : "Editing ";

  // If there's no diff content, use the simple card
  if (!oldStr || !newStr) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={Edit3}
        title={
          <>
            {title}
            {fileLink}
          </>
        }
        className="min-w-0 border rounded-md mb-0"
      />
    );
  }

  // If there's diff content, show an expandable card with the diff in expanded state
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Edit3}
      title={title}
      additionalContent={fileLink}
      className="min-w-0 mb-0"
      accordionValue="string-replace"
    >
      <DiffView oldStr={oldStr} newStr={newStr} />
    </ExpandableMessageCard>
  );
}

export function DiffView({
  oldStr,
  newStr,
}: {
  oldStr: string;
  newStr: string;
}) {
  const diff = useMemo(() => diffLines(oldStr, newStr), [oldStr, newStr]);

  if (oldStr === newStr) {
    return (
      <div className="text-muted-foreground text-sm justify-center items-center flex">
        <span>No changes</span>
      </div>
    );
  }

  return (
    <pre className="text-muted-foreground text-sm overflow-x-auto overflow-auto min-w-0">
      {diff.map((part, idx) => {
        const key = `${part.value}-${part.added ? "added" : part.removed ? "removed" : "normal"}-${idx}`;
        if (part.added) {
          return (
            <span key={key} className="bg-success/10 text-success">
              {part.value}
            </span>
          );
        }
        if (part.removed) {
          return (
            <span key={key} className="text-danger line-through">
              {part.value}
            </span>
          );
        }

        return <span key={key}>{part.value}</span>;
      })}
    </pre>
  );
}
